/**
 * ObjectField.tsx — which engineering object this symbol represents.
 *
 * A symbol on a sheet is a REPRESENTATION. The thing being represented is a
 * model object with a tag and a type, and it exists whether or not anyone has
 * drawn it. So placement asks for the object first, and offers the ones the
 * project already has before offering to create a new one.
 *
 * The list comes from a project query, not from a client-side model: the
 * browser holds no inventory of its own.
 */

import { useMemo, useState } from "react";
import { useClient } from "../../api/ClientContext";
import { Panel } from "../../components/Panel";
import { useAsync } from "../../stores/useAsync";
import { isAuthoringId } from "../authoring/operations";

interface CandidateObject { objectId: string; label: string }

export function ObjectField({ projectId, value, onChange, required }: {
  projectId: string;
  value: string;
  onChange: (objectId: string) => void;
  required?: boolean;
}): JSX.Element {
  const client = useClient();
  const [filter, setFilter] = useState("");
  const inventory = useAsync(() => client.runQuery(projectId, "inventory.filter", {}), [client, projectId]);

  const candidates = useMemo(() => extractObjects(inventory.data), [inventory.data]);
  const visible = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    return (needle ? candidates.filter(entry => entry.label.toLowerCase().includes(needle)) : candidates).slice(0, 60);
  }, [candidates, filter]);

  const legal = value === "" || isAuthoringId(value);

  return (
    <Panel title="Object">
      <p className="muted">
        A symbol represents something the plant has. Choose the object it stands for.
      </p>
      <input
        type="text" value={value} placeholder="object id"
        onChange={event => onChange(event.target.value)}
        aria-label="Object id"
        className={legal ? undefined : "invalid"}
      />
      {!legal && <p className="bad">An object id may use letters, digits, hyphen and underscore only.</p>}
      {required && !value.trim() && <p className="bad">Choose an object before placing.</p>}

      <input
        type="search" value={filter} placeholder="Search the project's objects"
        onChange={event => setFilter(event.target.value)}
        aria-label="Search objects"
      />
      {inventory.loading ? <p className="muted">Loading objects…</p> : (
        <ul className="objects">
          {visible.map(entry => (
            <li key={entry.objectId}>
              <button type="button" className={entry.objectId === value ? "is-armed" : undefined} onClick={() => onChange(entry.objectId)}>
                {entry.label}
              </button>
            </li>
          ))}
          {visible.length === 0 && <li className="muted">No objects matched. Type an id to use a new one.</li>}
        </ul>
      )}
    </Panel>
  );
}

/**
 * Pull object ids out of a query result.
 *
 * Deliberately defensive: the query response is an Engine shape this repository
 * does not own, and a client that hard-codes its internals would break on a
 * change it has no say in. Anything that does not look like an object is
 * skipped rather than guessed at.
 */
export function extractObjects(payload: unknown): CandidateObject[] {
  const found = new Map<string, CandidateObject>();

  const visit = (node: unknown, depth: number): void => {
    if (depth > 6 || node === null || typeof node !== "object") return;
    if (Array.isArray(node)) { for (const item of node) visit(item, depth + 1); return; }

    const record = node as Record<string, unknown>;
    const id = typeof record.objectId === "string" ? record.objectId
      : typeof record.id === "string" ? record.id
      : undefined;
    if (id && isAuthoringId(id)) {
      const tag = typeof record.tag === "string" ? record.tag : undefined;
      const name = typeof record.name === "string" ? record.name : undefined;
      const type = typeof record.objectType === "string" ? record.objectType : undefined;
      const label = [tag ?? id, name, type].filter(Boolean).join(" · ");
      if (!found.has(id)) found.set(id, { objectId: id, label });
    }
    for (const nested of Object.values(record)) visit(nested, depth + 1);
  };

  visit(payload, 0);
  return [...found.values()].sort((a, b) => a.label.localeCompare(b.label));
}
